import { DraftTeam, GameFranchise, GamePlayer, RosterLimits } from '../types/games';

export const ROSTER_LIMITS: RosterLimits = {
  QB: 2,
  RB: 2,
  WR: 3,
  TE: 1,
};

export const rosterSize = (limits: RosterLimits) => Object.values(limits)
  .reduce((total, count) => total + count, 0);

export const createDraftOrder = (picksPerTeam: number): number[] => {
  if (picksPerTeam <= 0) return [];

  const result = [0];
  const pickCounts = [1, 0];
  let team = 1;
  while (pickCounts[0] < picksPerTeam || pickCounts[1] < picksPerTeam) {
    for (let pick = 0; pick < 2 && pickCounts[team] < picksPerTeam; pick += 1) {
      result.push(team);
      pickCounts[team] += 1;
    }
    team = team === 0 ? 1 : 0;
  }
  return result;
};

export const DRAFT_ORDER = createDraftOrder(rosterSize(ROSTER_LIMITS));
export const AUTOSUGGEST_MIN_CHARACTERS = 3;

export const shuffle = <T,>(items: T[], random: () => number = Math.random): T[] => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
};

export const samplePlayers = (players: GamePlayer[], count = 8) => shuffle(players).slice(0, count);

export const remainingSlots = (team: DraftTeam, limits: RosterLimits = ROSTER_LIMITS): RosterLimits => {
  const result = { ...limits };
  team.Players.forEach((player) => {
    result[player.Position] = Math.max(0, result[player.Position] - 1);
  });
  return result;
};

export const eligiblePlayers = (
  franchise: GameFranchise,
  team: DraftTeam,
  draftedIds: Set<number>,
  limits: RosterLimits = ROSTER_LIMITS,
): GamePlayer[] => {
  const remaining = remainingSlots(team, limits);
  return franchise.Players
    .filter((player) => remaining[player.Position] > 0 && !draftedIds.has(player.PlayerId))
    .sort((first, second) => (
      second.PointsPerGame - first.PointsPerGame
      || second.GamesStarted - first.GamesStarted
      || first.Name.localeCompare(second.Name)
    ));
};

export const searchEligiblePlayers = (
  franchise: GameFranchise,
  team: DraftTeam,
  draftedIds: Set<number>,
  query: string,
  limits: RosterLimits = ROSTER_LIMITS,
): GamePlayer[] => {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (normalizedQuery.length < AUTOSUGGEST_MIN_CHARACTERS) return [];

  return eligiblePlayers(franchise, team, draftedIds, limits)
    .filter((player) => player.Name.toLocaleLowerCase().includes(normalizedQuery))
    .sort((first, second) => first.Name.localeCompare(second.Name));
};

export const createFranchiseSequence = (
  franchises: GameFranchise[],
  pickCount = DRAFT_ORDER.length,
): GameFranchise[] => {
  const result: GameFranchise[] = [];
  while (result.length < pickCount) {
    const bag = shuffle(franchises);
    if (result.length > 0 && bag.length > 1 && bag[0].Id === result[result.length - 1].Id) {
      [bag[0], bag[1]] = [bag[1], bag[0]];
    }
    result.push(...bag);
  }
  return result.slice(0, pickCount);
};

export const createFranchiseReel = (
  franchises: GameFranchise[],
  target: GameFranchise,
  rounds = 2,
  random: () => number = Math.random,
): GameFranchise[] => {
  const result: GameFranchise[] = [];
  for (let round = 0; round < rounds; round += 1) {
    const bag = shuffle(franchises, random);
    if (result.length > 0 && bag.length > 1 && result[result.length - 1].Id === bag[0].Id) {
      [bag[0], bag[1]] = [bag[1], bag[0]];
    }
    result.push(...bag);
  }

  if (result.length > 1 && result[result.length - 1].Id === target.Id) {
    [result[result.length - 2], result[result.length - 1]] = [
      result[result.length - 1],
      result[result.length - 2],
    ];
  }
  return [...result, target];
};

export const teamScore = (team: DraftTeam) => team.Players
  .reduce((total, player) => total + player.PointsPerGame, 0);
